import { useCallback } from "react";
import { Button, useModal, useToast } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { useOrderActions } from "../model/use.order.actions";

/** Cancels jobs once confirmed — it can't be undone. `onDone` gets whether all were cancelled. */
export function useCancelJobs() {
  const { openModal, closeModal } = useModal();
  const { toast } = useToast();
  const { handleCancelOrder, isLoading } = useOrderActions();
  const t = useT();

  const cancelJobs = useCallback(
    (jobs: ConnectProto.Job[], onDone?: (allCancelled: boolean) => void) => {
      const single = jobs.length === 1;
      const cancel = async () => {
        const cancelled = (await Promise.all(jobs.map(handleCancelOrder))).filter(Boolean).length;
        // A single failure is already reported by the global error toaster
        if (!single && cancelled < jobs.length) {
          toast({
            title: t(Namespace.Orders, "actions.cancelJobsFailedTitle"),
            description: t(Namespace.Orders, "actions.cancelJobsFailedBody", {
              cancelled,
              total: jobs.length,
            }),
            variant: "destructive",
          });
        }
        onDone?.(cancelled === jobs.length);
      };
      const modalId = openModal({
        size: "sm",
        title: t(
          Namespace.Orders,
          single ? "actions.cancelOrder" : "actions.cancelJobsConfirmTitle",
        ),
        body: (
          <span data-testid="cancel-jobs-message">
            {single
              ? t(Namespace.Orders, "actions.cancelJobConfirmBody")
              : t(Namespace.Orders, "actions.cancelJobsConfirmBody", { count: jobs.length })}
          </span>
        ),
        footer: (
          <div className="flex justify-end gap-2 w-full">
            <Button
              variant="outline"
              onClick={() => closeModal(modalId)}
              data-testid="cancel-jobs-dismiss"
            >
              {t(Namespace.Common, "cancel")}
            </Button>
            <Button
              variant="opacityRedWithOutline"
              data-testid="cancel-jobs-confirm"
              onClick={() => {
                closeModal(modalId);
                cancel();
              }}
            >
              {t(Namespace.Orders, "actions.cancelOrder")}
            </Button>
          </div>
        ),
      });
    },
    [openModal, closeModal, toast, handleCancelOrder, t],
  );

  return { cancelJobs, isCancelling: isLoading };
}
